import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, ArrowUpDown, ChevronLeft, Plus, Download, 
  FileText, Clock, CheckCircle, Truck, Package, AlertTriangle, BarChart3
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const PurchaseOrderList = ({ onBack, onSelectPO, onCreatePO }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from Supabase
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch purchase orders
      const { data: poData, error: poError } = await supabase
        .from('purchase_orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (poError) throw poError;

      // Fetch suppliers
      const { data: supplierData, error: supplierError } = await supabase
        .from('suppliers')
        .select('*');

      if (supplierError) throw supplierError;

      setPurchaseOrders(poData || []);
      setSuppliers(supplierData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get supplier name by ID
  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.name : supplierId;
  };

  // Get status details
  const getStatusDetails = (status) => {
    const statusMap = {
      'draft': { name: '草稿', color: '#6B7280', icon: FileText },
      'sent': { name: '已發送', color: '#3B82F6', icon: Truck },
      'approved': { name: '已核准', color: '#10B981', icon: CheckCircle },
      'received': { name: '已收貨', color: '#059669', icon: Package },
      'partially_received': { name: '部分收貨', color: '#8B5CF6', icon: Package },
      'cancelled': { name: '已取消', color: '#EF4444', icon: AlertTriangle }
    };
    
    return statusMap[status] || { name: '未知', color: '#6B7280', icon: FileText };
  };

  // Purchase order statuses for filter dropdown
  const purchaseOrderStatuses = [
    { id: 'draft', name: '草稿' },
    { id: 'sent', name: '已發送' },
    { id: 'approved', name: '已核准' },
    { id: 'received', name: '已收貨' },
    { id: 'partially_received', name: '部分收貨' },
    { id: 'cancelled', name: '已取消' }
  ];

  // Filter and sort purchase orders
  const filteredPOs = purchaseOrders
    .filter(po => {
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          (po.po_number || '').toLowerCase().includes(searchLower) ||
          getSupplierName(po.supplier_id).toLowerCase().includes(searchLower) ||
          (po.notes || '').toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .filter(po => {
      // Apply status filter
      if (statusFilter !== 'all') {
        return po.status === statusFilter;
      }
      return true;
    })
    .sort((a, b) => {
      // Apply sorting
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle date fields
      if (sortField === 'created_at' || sortField === 'order_date' || sortField === 'expected_delivery_date') {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }
      
      // Handle supplier name
      if (sortField === 'supplier_id') {
        aValue = getSupplierName(aValue);
        bValue = getSupplierName(bValue);
      }
      
      // Compare values based on sort direction
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Handle sort click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading purchase orders...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <button 
            onClick={onBack} 
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ChevronLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">採購訂單列表</h1>
        </div>
        
        <div className="flex flex-col md:flex-row gap-3">
          <button 
            onClick={onCreatePO} 
            className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md flex items-center justify-center space-x-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>新增訂單</span>
          </button>
          
          <button className="border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-md flex items-center justify-center space-x-2 transition-colors">
            <Download className="h-4 w-4 text-gray-600" />
            <span className="text-gray-600">匯出列表</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="搜尋訂單編號、供應商或備註..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="w-full md:w-64">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                className="block w-full pl-10 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-md appearance-none"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">所有狀態</option>
                {purchaseOrderStatuses.map(status => (
                  <option key={status.id} value={status.id}>{status.name}</option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <ChevronLeft className="h-5 w-5 text-gray-400 transform rotate-270" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Orders Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <tr>
                <th 
                  className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('po_number')}
                >
                  <div className="flex items-center space-x-1">
                    <span>訂單編號</span>
                    {sortField === 'po_number' && (
                      <ArrowUpDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('supplier_id')}
                >
                  <div className="flex items-center space-x-1">
                    <span>供應商</span>
                    {sortField === 'supplier_id' && (
                      <ArrowUpDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center space-x-1">
                    <span>建立日期</span>
                    {sortField === 'created_at' && (
                      <ArrowUpDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('expected_delivery_date')}
                >
                  <div className="flex items-center space-x-1">
                    <span>預計交期</span>
                    {sortField === 'expected_delivery_date' && (
                      <ArrowUpDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>狀態</span>
                    {sortField === 'status' && (
                      <ArrowUpDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-right cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('total')}
                >
                  <div className="flex items-center justify-end space-x-1">
                    <span>金額</span>
                    {sortField === 'total' && (
                      <ArrowUpDown className="h-4 w-4" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-center">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white text-sm">
              {filteredPOs.length > 0 ? (
                filteredPOs.map(po => {
                  const status = getStatusDetails(po.status);
                  const Icon = status.icon;
                  
                  return (
                    <tr key={po.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium">{po.po_number}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{getSupplierName(po.supplier_id)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(po.created_at || po.order_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(po.expected_delivery_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div 
                            className="p-1 rounded-full mr-2"
                            style={{ backgroundColor: status.color + '20' }}
                          >
                            <Icon className="h-4 w-4" style={{ color: status.color }} />
                          </div>
                          <span style={{ color: status.color }}>{status.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                        ${(po.total || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button 
                          onClick={() => onSelectPO(po.id)}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          查看
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    沒有找到符合條件的採購訂單
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderList;
